// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyLangValue, TyLatLng, TyProjectRef, TyStatusLock, TyUnit, TyUnitFlags } from "@/types";
import Icon from "@/UI/atoms/Icon";
import ChoiceInput from "@/UI/inputs/ChoiceInput";
import FileInput from "@/UI/inputs/FileInput";
import MapInput from "@/UI/inputs/MapInput";
import FieldsGroup from "@/UI/components/FieldsGroup";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import NumberInput from "@/UI/inputs/NumberInput";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
import RenamePopup from "@/UI/popups/RenamePopup";
import UnitFieldsPopup, { type TyUnitFieldValues } from "@/UI/popups/UnitFieldsPopup";
// --- Logic --------------------------------------------------------------------------------------
export type TyUnitFormDetails = {
    /** Editing one, or — when `isDuplicate` — using one as the starting point for a new unit. */
    unit?: TyUnit | null;
    isDuplicate?: boolean;
    project: TyProjectRef;
    statusLock?: TyStatusLock;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

type TyNumbers = Record<string, number | null>;

/** Starts every count and measure at "nobody said", which is not the same as zero. */
const emptyGroup = (keys: readonly string[]): TyNumbers => Object.fromEntries(keys.map((key) => [key, null]));
const emptyFlags = (): TyUnitFlags => Object.fromEntries(constants.unitGroups.flags.map((key) => [key, null])) as TyUnitFlags;

/**
 * Create, edit, or duplicate a unit — forty-odd fields, which is exactly why they are grouped.
 *
 * **Every group renders from `constants.unitGroups`.** Fourteen counts and fifteen flags are drawn by
 * one `map` each, so adding an attribute is one line in the model and one in the constants, never a
 * hand-written input buried in here.
 *
 * Duplicating reads a unit and opens this form filled in with everything **except the number**, which
 * is the one field that must differ. That is how a floor of near-identical flats gets entered.
 */
export default function UnitFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { unit, isDuplicate, project, statusLock, onDone } = details as TyUnitFormDetails;
    const enqueue = useUploads((state) => state.enqueue);
    const addComp = usePopup((state) => state.addComp);
    const isEdit = !!unit && !isDuplicate;

    const [number, setNumber] = useState(isEdit ? unit!.number : "");
    const [building, setBuilding] = useState(unit?.building ?? "");
    /** Local, because a building can be created from this form and must appear in the list at once. */
    const [buildings, setBuildings] = useState(project.buildings);
    const [floor, setFloor] = useState<number | null>(unit?.floor ?? null);
    const [type, setType] = useState<string>(unit?.type ?? "");
    const [usage, setUsage] = useState<string>(unit?.usage ?? "");
    const [offer, setOffer] = useState<string>(unit?.offer ?? "");
    const [status, setStatus] = useState<string>(unit?.status ?? "available");
    const [price, setPrice] = useState<number | null>(unit?.price ?? null);
    const [rentPeriod, setRentPeriod] = useState<number | null>(unit?.rentPeriod ?? null);
    const [deedNumber, setDeedNumber] = useState(unit?.deedNumber ?? "");
    const [description, setDescription] = useState<TyLangValue[]>(unit?.description ?? []);
    const [measures, setMeasures] = useState<TyNumbers>({ ...emptyGroup(constants.unitGroups.measures), ...(unit?.measures ?? {}) });
    const [counts, setCounts] = useState<TyNumbers>({ ...emptyGroup(constants.unitGroups.counts), ...(unit?.counts ?? {}) });
    const [flags, setFlags] = useState<TyUnitFlags>({ ...emptyFlags(), ...(unit?.flags ?? {}) });
    const [acSystem, setAcSystem] = useState<string>(unit?.choices.acSystem ?? "");
    const [facades, setFacades] = useState<string[]>(unit?.choices.facades ?? []);
    const [location, setLocation] = useState<TyLatLng | null>(unit?.location ?? project.location ?? null);
    const [useProjectGallery, setUseProjectGallery] = useState(unit?.useProjectGallery ?? true);
    const [isPublic, setIsPublic] = useState(!!unit?.isPublic);
    const [notes, setNotes] = useState(unit?.notes ?? "");
    const [photos, setPhotos] = useState<File[]>([]);
    const [formError, setFormError] = useState<string | null>(null);

    const path = isEdit ? `/properties/units/${unit!._id}` : "/properties/units";
    const { data, loading, error, succeededAt, invoke } = useFetch<{ unit: TyUnit }>(path, {}, false);

    useEffect(() => {
        if (!succeededAt || !data) return;
        if (photos.length) enqueue(photos, { model: "Unit", id: data.unit._id, slot: "gallery" });
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        setFormError(null);
        if (!number.trim()) return setFormError(t.properties.numberRequired);

        invoke({
            method: isEdit ? "PATCH" : "POST",
            data: {
                project: project._id,
                building: building || null,
                number: number.trim(),
                floor,
                type: type || null,
                usage: usage || null,
                offer: offer || null,
                // An edit sends the status only when sales does not own it; create never sends one.
                ...(isEdit && !statusLock ? { status } : {}),
                price,
                rentPeriod,
                deedNumber: deedNumber.trim() || null,
                description,
                measures,
                counts,
                flags,
                choices: { acSystem: acSystem || null, facades },
                location,
                useProjectGallery,
                isPublic,
                notes: notes.trim() || null,
            },
        });
    };

    const title = isEdit ? t.properties.editUnit : isDuplicate ? t.properties.duplicateUnit : t.properties.addUnit;

    /**
     * Add a block without leaving the unit. Creating one returns the project's whole building list,
     * so the new one can be selected straight away rather than fetched back out of the project.
     */
    const openAddBuilding = () =>
        addComp({
            name: "add-building",
            Component: RenamePopup,
            anim: "scale",
            details: {
                title: t.properties.addBuilding,
                value: "",
                path: `/properties/projects/${project._id}/buildings`,
                method: "POST",
                onDone: (data: unknown) => {
                    const next = (data as { buildings?: typeof buildings } | undefined)?.buildings;
                    if (!next?.length) return;
                    setBuildings(next);
                    // The one it did not have before is the one just typed.
                    const created = next.find((entry) => !buildings.some((old) => old._id === entry._id));
                    if (created) setBuilding(created._id);
                },
            },
        });

    const openFields = (kind: "counts" | "flags") =>
        addComp({
            name: `unit-${kind}`,
            Component: UnitFieldsPopup,
            anim: "scale",
            details: {
                title: kind === "counts" ? t.properties.groups.counts : t.properties.groups.flags,
                keys: constants.unitGroups[kind],
                labels: kind === "counts" ? t.properties.counts : t.properties.flags,
                kind,
                values: kind === "counts" ? counts : flags,
                onConfirm: (values: TyUnitFieldValues) => (kind === "counts" ? setCounts(values as TyNumbers) : setFlags(values as TyUnitFlags)),
            },
        });

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{title}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {/* What identifies the unit: which one, and where it sits. */}
                <div className="side-form__row side-form__row--pair">
                    <TextInput value={number} onChange={setNumber} label={t.properties.fields.number} disabled={loading} />
                    {/* Always offered, even on a project with no blocks yet: adding the first one
                        from here is the point of the button beside it. */}
                    <div className="side-form__with-add side-form__with-add--joined">
                        <SelectInput
                            value={building}
                            onChange={setBuilding}
                            options={buildings.map((entry) => ({ value: entry._id, label: entry.name }))}
                            label={t.properties.fields.building}
                            placeholder={buildings.length ? t.properties.notSet : t.properties.emptyBuildingsShort}
                            disabled={loading || !buildings.length}
                        />
                        <button
                            type="button"
                            className="side-form__add-beside"
                            onClick={openAddBuilding}
                            disabled={loading}
                            aria-label={t.properties.addBuilding}
                            title={t.properties.addBuilding}
                        >
                            <Icon name="add" size={16} />
                        </button>
                    </div>
                </div>

                <div className="side-form__row side-form__row--pair">
                    <NumberInput value={floor} onChange={setFloor} label={t.properties.fields.floor} disabled={loading} />
                    <SelectInput
                        value={type}
                        onChange={setType}
                        options={constants.unitTypes.map((item) => ({ value: item, label: t.properties.unitTypes[item] }))}
                        label={t.properties.fields.type}
                        placeholder={t.properties.notSet}
                        disabled={loading}
                    />
                </div>

                <div className="side-form__row">
                    <ChoiceInput
                        value={offer}
                        onChange={setOffer}
                        options={constants.unitOffers.map((item) => ({ value: item, label: t.properties.offers[item] }))}
                        label={t.properties.fields.offer}
                        disabled={loading}
                    />
                    <NumberInput
                        value={price}
                        onChange={setPrice}
                        label={t.properties.fields.price}
                        suffix={t.properties.currency}
                        disabled={loading}
                    />
                    {/* Only under rent — a sale price covers no period, and the API clears it anyway. */}
                    {offer === "rent" && (
                        <NumberInput
                            value={rentPeriod}
                            onChange={setRentPeriod}
                            label={t.properties.fields.rentPeriod}
                            placeholder={t.properties.fields.rentPeriodHint}
                            suffix={t.properties.months}
                            min={1}
                            disabled={loading}
                        />
                    )}
                </div>

                <div className="side-form__row side-form__row--pair">
                    <TextInput value={deedNumber} onChange={setDeedNumber} label={t.properties.fields.deedNumber} disabled={loading} />
                    <ChoiceInput
                        value={usage}
                        onChange={setUsage}
                        options={constants.unitUsages.map((item) => ({ value: item, label: t.properties.usages[item] }))}
                        label={t.properties.fields.usage}
                        disabled={loading}
                    />
                </div>

                {isEdit && (
                    <SelectInput
                        value={status}
                        onChange={setStatus}
                        options={constants.unitStatuses.map((item) => ({ value: item, label: t.properties.statuses[item] }))}
                        label={t.properties.fields.status}
                        // Sales owns it while a contract or reservation is live — say so, do not just grey it.
                        disabled={loading || !!statusLock}
                    />
                )}
                {isEdit && statusLock && <p className="side-form__hint">{t.properties.statusLocked}</p>}

                {/* --- The groups, each drawn from the constants, never by hand ------------------ */}
                <div className="side-form__group">
                    <span className="side-form__group-title">{t.properties.groups.measures}</span>
                    <div className="side-form__grid">
                        {constants.unitGroups.measures.map((key) => (
                            <NumberInput
                                key={key}
                                value={measures[key] ?? null}
                                onChange={(value) => setMeasures((current) => ({ ...current, [key]: value }))}
                                label={t.properties.measures[key]}
                                suffix={t.properties.sqm}
                                disabled={loading}
                            />
                        ))}
                    </div>
                </div>

                {/* Twenty-nine inputs between them, and almost no unit fills eight. What has been
                    answered is shown; answering happens in a popup, so the form is not a scroll
                    through blanks with the four that matter lost among them. */}
                <FieldsGroup
                    title={t.properties.groups.counts}
                    entries={constants.unitGroups.counts
                        .filter((key) => counts[key] !== null)
                        .map((key) => ({ key, label: t.properties.counts[key], count: counts[key] as number }))}
                    onOpen={() => openFields("counts")}
                    disabled={loading}
                />

                <FieldsGroup
                    title={t.properties.groups.flags}
                    entries={constants.unitGroups.flags
                        .filter((key) => flags[key] !== null)
                        .map((key) => ({ key, label: t.properties.flags[key], on: !!flags[key] }))}
                    onOpen={() => openFields("flags")}
                    disabled={loading}
                />

                <div className="side-form__group">
                    <span className="side-form__group-title">{t.properties.groups.choices}</span>

                    {/* One picks one word, the other picks up to four. The air conditioning keeps a
                        word's width and the facades take the rest, which is where they are needed. */}
                    <div className="side-form__row side-form__row--split">
                        <MultiSelectInput
                            value={facades}
                            onChange={setFacades}
                            options={constants.unitFacades.map((item) => ({ value: item, label: t.properties.facades[item] }))}
                            label={t.properties.choices.facades}
                            disabled={loading}
                        />
                        <SelectInput
                            value={acSystem}
                            onChange={setAcSystem}
                            options={constants.acSystems.map((item) => ({ value: item, label: t.properties.acSystems[item] }))}
                            label={t.properties.choices.acSystem}
                            placeholder={t.properties.notSet}
                            disabled={loading}
                        />
                    </div>
                </div>

                <MultiLangInput value={description} onChange={setDescription} label={t.properties.fields.description} multiline disabled={loading} />

                <MapInput value={location} onChange={setLocation} label={t.properties.fields.location} disabled={loading} />

                {/*
                 * The two answers about how this unit is **shown**: whose pictures it uses, and
                 * whether anybody outside the company sees it at all. A line each, full width —
                 * side by side they squeezed to two narrow boxes in a panel that is already narrow.
                 * No caption under either: the label is the whole question.
                 *
                 * The gallery answer sits **above** the file picker because it decides whether that
                 * picker exists: a unit showing the project's photographs has nothing to upload here,
                 * and a box asking for pictures that will not be used is a box somebody fills in by
                 * mistake. Turning the toggle off brings it back.
                 */}
                <label className={`side-form__toggle${useProjectGallery ? " side-form__toggle--on" : ""}`}>
                    <input
                        type="checkbox"
                        checked={useProjectGallery}
                        onChange={(event) => {
                            setUseProjectGallery(event.target.checked);
                            // Hiding the picker has to drop what is in it, or files nobody can see any
                            // more would still go up to S3 when the form is saved.
                            if (event.target.checked) setPhotos([]);
                        }}
                        disabled={loading}
                    />
                    <span className="side-form__toggle-icon">
                        <Icon name="image" size={18} />
                    </span>
                    <span className="side-form__toggle-text">{t.properties.fields.useProjectGallery}</span>
                    <span className="side-form__toggle-mark" aria-hidden="true">
                        <Icon name="check" size={12} />
                    </span>
                </label>

                <label className={`side-form__toggle${isPublic ? " side-form__toggle--on" : ""}`}>
                    <input type="checkbox" checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} disabled={loading} />
                    <span className="side-form__toggle-icon">
                        <Icon name="eye" size={18} />
                    </span>
                    <span className="side-form__toggle-text">{t.properties.fields.showInCatalog}</span>
                    <span className="side-form__toggle-mark" aria-hidden="true">
                        <Icon name="check" size={12} />
                    </span>
                </label>

                {!isEdit && !useProjectGallery && <FileInput files={photos} onChange={setPhotos} accept="image/*" />}

                {/* Last, always. Notes are what somebody writes once the form is answered, and a
                    page's worth of textarea in the middle of forty fields buries what follows it. */}
                <TextInput value={notes} onChange={setNotes} label={t.properties.fields.notes} multiline disabled={loading} />

                {(formError || error) && <p className="side-form__error">{formError ?? error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
